import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUploadOutlined";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { Alert } from "@material-ui/lab";
import Papa from "papaparse";
import { useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { individualDummyData, organisationDummyData } from "../../constants";
import CsvDownloader from "./CsvDownloader";

export const CsvUploader = ({ onDataProcessed }) => {
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const text = await file.text();

        Papa.parse(text, {
          complete: (result) => {
            const jsonData = result.data;
            const structuredData = jsonData;
            if (onDataProcessed) {
              onDataProcessed(structuredData[0]);
            }
          },
          header: true,
          skipEmptyLines: true,
        });
      } catch (error) {
        console.error("Error while parsing CSV:", error.message);
        alert("Failed to parse CSV.");
      }
    }
  };

  return (
    <label className="h-[24px] px-2.5 bg-[#313C4C] hover:bg-[#1e2632] text-white text-[11px] font-medium rounded-none flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap">
      <CloudUploadIcon style={{ fontSize: 14, color: "#ffffff" }} />
      <span>Upload CSV</span>
      <input type="file" accept=".csv" hidden onChange={handleFileUpload} />
    </label>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    width: "100%",
    gap: theme.spacing(1),
  },
  actionContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  uploadContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  dropZone: {
    padding: theme.spacing(4),
    width: "100%",
    textAlign: "center",
  },
}));

const FIELD_MAPPINGS = {
  individual_title: ["Title", "Prefix", "Salutation", "Name Prefix"],
  individual_firstName: ["First Name", "Given Name", "firstname", "First"],
  individual_middleName: ["Middle Name", "Middle"],
  individual_lastName: ["Last Name", "Family Name", "lastname", "Surname"],
  individual_webPage: [
    "Website",
    "Web Page",
    "URL",
    "Homepage",
    "Personal Website",
    "Website 1 - Value",
  ],
  individual_gender: ["Gender", "Sex"],
  individual_birthdate: [
    "Birthday",
    "Birth Date",
    "Date of Birth",
    "DOB",
    "Birthdate",
    "Event 1 - Value",
  ],
  individual_anniversary: [
    "Anniversary",
    "Wedding Date",
    "Anniversary Date",
    "Event 1 - Value",
    "Event 2 - Value",
    "Event 3 - Value",
  ],
  individual_emailPersonal: [
    "Email Address",
    "E-mail Address",
    "Email 1 Address",
    "Primary Email",
    "Personal Email",
    "Email",
    "email",
    "E-mail 1 - Value",
  ],
  individual_emailPersonal2: [
    "Email 2 Address",
    "Secondary Email",
    "Alternate Email",
    "E-mail 2 - Value",
  ],
  individual_mobilePersonal: [
    "Phone Number",
    "Mobile Phone",
    "Primary Phone",
    "Phone",
    "Mobile",
    "Cell Phone",
    "Cellular",
    "Personal Phone",
    "Home Phone",
    "Phone 1 - Value",
  ],
  individual_mobilePersonal2: [
    "Phone 2",
    "Secondary Phone",
    "Alternate Phone",
    "Mobile 2",
    "Phone 2 - Value",
  ],
  individual_homeAddress: [
    "Street Address",
    "Address",
    "Home Street",
    "Home Address",
    "Personal Address",
    "Address 1 - Street",
  ],
  individual_personal_city: [
    "City",
    "Home City",
    "Personal City",
    "Address 1 - City",
  ],
  individual_personal_state: [
    "State",
    "Province",
    "Home State",
    "Personal State",
    "Address 1 - Region",
  ],
  individual_personal_postalCode: [
    "Zip Code",
    "Postal Code",
    "Post Code",
    "Address 1 - Postal Code",
  ],
  individual_personal_country: [
    "Country",
    "Home Country",
    "Personal Country",
    "Address 1 - Country",
  ],
  individual_shortName: ["Nickname", "Short Name", "Display Name"],
  individual_message: [
    "Notes",
    "Comments",
    "Additional Info",
    "Message",
    "Description",
  ],
  individual_company: [
    "Company",
    "Organization Name",
    "Organization",
    "Employer",
    "Business",
  ],
  individual_jobTitle: [
    "Job Title",
    "Title",
    "Position",
    "Role",
    "Organization Title",
  ],
  individual_department: [
    "Department",
    "Division",
    "Team",
    "Organization Department",
  ],
  individual_mobileNoOfficial: [
    "Business Phone",
    "Work Phone",
    "Office Phone",
    "Business Mobile",
  ],
  individual_emailOfficial: [
    "Business Email",
    "Work Email",
    "Office Email",
    "Company Email",
    "E-mail 1 - Value",
    "Email",
    "E-mail Address",
    "Email Address",
  ],
  individual_officeAddress: [
    "Business Street",
    "Work Address",
    "Office Address",
    "Business Address",
  ],
  individual_professional_city: ["Business City", "Work City", "Office City"],
  individual_professional_state: [
    "Business State",
    "Work State",
    "Office State",
  ],
  individual_professional_postalCode: [
    "Business Postal Code",
    "Work Postal Code",
    "Office Postal Code",
  ],
  individual_professional_country: [
    "Business Country",
    "Work Country",
    "Office Country",
  ],
  organisation_fullName: [
    "Organization Name",
    "Company Name",
    "Business Name",
    "Company",
    "Organization",
  ],
  organisation_contactPerson: [
    "Contact Person",
    "Primary Contact",
    "Main Contact",
  ],
  organisation_mobileOfficial: [
    "Phone Number",
    "Business Phone",
    "Office Phone",
    "Main Phone",
  ],
  organisation_officeAddress: [
    "Address",
    "Business Address",
    "Office Address",
    "Street Address",
  ],
  organisation_emailOfficial: [
    "Email Address",
    "Business Email",
    "Office Email",
    "Main Email",
  ],
  organisation_city: ["City", "Business City", "Office City"],
  organisation_state: ["State", "Province", "Business State"],
  organisation_postalCode: ["Zip Code", "Postal Code", "Post Code"],
  organisation_country: ["Country", "Business Country"],
  organisation_shortName: ["Short Name", "Nickname", "Abbreviated Name"],
  organisation_notes: ["Notes", "Comments", "Description", "Additional Info"],
};
const INDIVIDUAL_KEYS = [
  "individual_title",
  "individual_firstName",
  "individual_middleName",
  "individual_lastName",
  "individual_webPage",
  "individual_gender",
  "individual_birthdate",
  "individual_anniversary",
  "individual_location",
  "individual_language",
  "individual_emailPersonal",
  "individual_emailPersonal2",
  "individual_mobilePersonal",
  "individual_mobilePersonal2",
  "individual_homeAddress",
  "individual_personal_city",
  "individual_personal_state",
  "individual_personal_postalCode",
  "individual_personal_country",
  "individual_shortName",
  "individual_message",
  "individual_contactType",
  "individual_company",
  "individual_jobTitle",
  "individual_department",
  "individual_officeLocation",
  "individual_mobileNoOfficial",
  "individual_mobileNoOfficial2",
  "individual_emailOfficial",
  "individual_emailOfficial2",
  "individual_emailOfficial3",
  "individual_officeAddress",
  "individual_professional_city",
  "individual_professional_state",
  "individual_professional_postalCode",
  "individual_professional_country",
  "individual_bankHolderName",
  "individual_bankAccountNumber",
  "individual_bankIFSC",
  "individual_branchName",
];
const ORGANISATION_KEYS = [
  "organisation_fullName",
  "organisation_fullStyle",
  "organisation_typeTags",
  "organisation_officeLocation",
  "organisation_contactPerson",
  "organisation_contactPerson2",
  "organisation_mobileOfficial",
  "organisation_mobileOfficial2",
  "organisation_officeAddress",
  "organisation_emailOfficial",
  "organisation_emailOfficial2",
  "organisation_emailOfficial3",
  "organisation_city",
  "organisation_state",
  "organisation_postalCode",
  "organisation_country",
  "organisation_shortName",
  "organisation_notes",
];
function mapHeaders(csvHeaders, selectedType) {
  const desiredKeys =
    selectedType === "Individual" ? INDIVIDUAL_KEYS : ORGANISATION_KEYS;
  const mapping = {};
  for (const key of desiredKeys) {
    const aliases = FIELD_MAPPINGS[key] || [];
    const foundHeader = csvHeaders.find((h) =>
      aliases.some((a) => h.trim().toLowerCase() === a.trim().toLowerCase())
    );
    mapping[key] = foundHeader || key;
  }
  return mapping;
}

function transformRow(row, headerMap, selectedType) {
  const desiredKeys =
    selectedType === "Individual" ? INDIVIDUAL_KEYS : ORGANISATION_KEYS;
  const result = {};
  for (const key of desiredKeys) {
    const csvKey = headerMap[key];
    let value = "";

    if (csvKey && row.hasOwnProperty(csvKey)) {
      value = row[csvKey] || "";
    } else {
      if (key === "individual_gender") value = "male";
      else if (key === "individual_birthdate") value = null;
      else if (key === "individual_contactType") value = [];
      else value = "";
    }
    result[key] = value;
  }
  result["type"] = selectedType;
  return result;
}

export const UploadCSVModal = ({
  onUploadSuccess,
  onCancel,
  onDataProcessed,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const [selectedType, setSelectedType] = useState("Individual");
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUploading, setFileUploading] = useState("");
  const [error, setError] = useState("");
  const MAX_ROWS = 3000;
  const fileTypes = ["CSV"];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = async (file) => {
    setError("");
    setFileUploading("");

    if (file.type !== "text/csv" && !file.name.toLowerCase().endsWith(".csv")) {
      setError("Please upload a valid CSV file");
      return;
    }

    try {
      setFileUploading("CSV file Uploading...");

      const text = await file.text();

      Papa.parse(text, {
        complete: (result) => {
          if (result.data.length > MAX_ROWS) {
            setError(
              `Maximum ${MAX_ROWS} rows allowed. Your file contains ${result.data.length} rows.`
            );
            setFileUploading("");
            return;
          }

          // Check if there are any rows at all
          if (result.data.length === 0) {
            setError("The CSV file is empty");
            setFileUploading("");
            return;
          }

          const csvHeaders = result.meta.fields;
          const headerMap = mapHeaders(csvHeaders, selectedType);

          const transformedRows = result.data.map((row) =>
            transformRow(row, headerMap, selectedType)
          );

          const emailKey =
            selectedType === "Individual"
              ? "individual_emailOfficial"
              : "organisation_emailOfficial";

          // Check if any rows are missing the email field entirely
          const rowsMissingEmail = transformedRows.filter(
            (row) => !row[emailKey] || row[emailKey].trim() === ""
          );

          if (rowsMissingEmail.length > 0) {
            setError(
              `${rowsMissingEmail.length} row(s) are missing the email field. Please ensure all rows have an email address.`
            );
            setFileUploading("");
            return;
          }

          // Now check for valid emails
          const validRows = transformedRows.filter((row) =>
            validateEmail(row[emailKey])
          );
          const invalidRows = transformedRows.filter(
            (row) => !validateEmail(row[emailKey])
          );

          // Show warning if there are invalid emails (but still allow upload)
          if (invalidRows.length > 0) {
            setError(
              `${invalidRows.length} row(s) have invalid email addresses. These rows will be skipped.`
            );
          }

          onDataProcessed?.({
            validData: validRows,
            invalidData: invalidRows,
            totalRows: result.data.length,
            type: selectedType,
          });

          setFileUploading("File Uploaded Successfully!");
          setSelectedFile(file);
        },
        header: true,
        skipEmptyLines: true,
      });
    } catch (err) {
      setError("Something went wrong during upload!");
      onCancel?.();
      setOpenUploadModal(false);
      setSelectedFile(null);
      setFileUploading("");
      console.error(err);
    }
  };

  const CustomSection = () => (
    <div className={classes.dropZone}>
      <Typography variant="body1" color={"textSecondary"}>
        {fileUploading ||
          `Drag & drop or click to select CSV files (Max ${MAX_ROWS} rows)`}
      </Typography>
    </div>
  );

  const handleCancel = () => {
    onCancel?.();
    setOpenUploadModal(false);
    setSelectedFile(null);
    setError("");
    setFileUploading("");
  };

  const handleUpload = () => {
    onUploadSuccess?.();
    setOpenUploadModal(false);
    onCancel?.();
    setSelectedFile(null);
    setError("");
    setFileUploading("");
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.actionContainer}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <FormControlLabel
              value="Individual"
              control={<Radio color="primary" size="small" />}
              label={<span className="text-[11px] font-medium text-gray-800">Individual</span>}
            />
            <FormControlLabel
              value="Organisation"
              control={<Radio color="primary" size="small" />}
              label={<span className="text-[11px] font-medium text-gray-800">Organisation</span>}
            />
          </RadioGroup>
        </FormControl>

        <CsvDownloader
          data={
            selectedType === "Individual"
              ? individualDummyData
              : organisationDummyData
          }
        />

        <button
          onClick={() => setOpenUploadModal(true)}
          className="h-[24px] px-2.5 bg-[#313C4C] hover:bg-[#1e2632] text-white text-[11px] font-medium rounded-none flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
        >
          <CloudUploadIcon style={{ fontSize: 14, color: "#ffffff" }} className="text-white" />
          <span>Upload CSV</span>
        </button>
      </Box>

      <Dialog
        open={openUploadModal}
        onClose={handleCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          className: "rounded-none overflow-hidden border border-slate-200 shadow-2xl",
        }}
      >
        {/* Compact Header Bar */}
        <div className="flex items-center justify-between w-full px-3 py-1.5 bg-[#f8fafc] border-b border-slate-200">
          <span className="text-slate-900 text-[13px] font-semibold tracking-wide">
            Upload {selectedType === "Individual" ? "Individual" : "Organisation"} CSV
          </span>
          <button
            onClick={handleCancel}
            className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-200/80 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
            title="Close"
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        </div>

        {/* Compact Content */}
        <DialogContent className="!p-3 bg-white space-y-2">
          {/* Drop Zone */}
          <div className="border border-dashed border-sky-300 bg-sky-50/50 hover:bg-sky-50 transition-colors p-3 text-center cursor-pointer">
            <FileUploader
              dropMessageStyle={{ color: "#000" }}
              children={
                <div className="py-2 text-center">
                  <CloudUploadIcon className="text-[#313C4C] mb-1" style={{ fontSize: 24 }} />
                  <p className="text-[11px] font-medium text-slate-700">
                    {fileUploading || `Drag & drop or click to select CSV file`}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Max {MAX_ROWS} rows | Format: .csv
                  </p>
                </div>
              }
              multiple={false}
              handleChange={handleChange}
              name="file"
              types={fileTypes}
            />
          </div>

          {/* CSV Import Tip */}
          <div className="flex items-center justify-center gap-1 text-[11px] text-slate-600 bg-slate-50 py-1 px-2 border border-slate-200">
            <Tooltip
              title={
                <span className="text-[11px]">
                  Import contacts from Google Contacts or Outlook by exporting them as CSV.
                  <br />
                  <b>Tip:</b> Columns should include Name, Email, Phone, Address.
                </span>
              }
              arrow
              placement="top"
            >
              <div className="flex items-center gap-1.5 cursor-pointer">
                <IoMdInformationCircleOutline size={14} className="text-[#313C4C]" />
                <span className="font-medium text-[#313C4C]">CSV Import Tip</span>
              </div>
            </Tooltip>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-rose-50 border border-rose-200 p-1.5 text-[11px] text-rose-700">
              {error}
            </div>
          )}

          {/* Selected File Badge */}
          {selectedFile && (
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 px-2 py-1 text-[11px]">
              <div className="flex items-center gap-1.5 text-emerald-800 font-medium">
                <InsertDriveFileIcon style={{ fontSize: 16 }} />
                <span className="truncate max-w-[200px]">{selectedFile.name}</span>
              </div>
              <span className="text-emerald-600 text-[10px]">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </span>
            </div>
          )}
        </DialogContent>

        {/* Footer Bar */}
        <div className="flex items-center justify-end gap-2 w-full px-3 py-1.5 bg-slate-50 border-t border-slate-200">
          <button
            onClick={handleCancel}
            className="h-[24px] px-3 text-[11px] font-medium text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 rounded-none cursor-pointer transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || !!error}
            className="h-[24px] px-3 text-[11px] font-medium text-white bg-[#313C4C] hover:bg-[#1e2632] border border-[#313C4C] rounded-none cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Upload File
          </button>
        </div>
      </Dialog>
    </Box>
  );
};
